import { hoursToMilliseconds } from 'date-fns';
import { z } from 'zod/v4';

const buildVersionFileSchema = z.object({
  version: z.string().min(1),
  builtAt: z.iso.datetime(),
});

export const BUILD_VERSION_FILE_PATH = '/version.json';

export const UPDATE_AVAILABLE_AFTER_LIVE_FOR = hoursToMilliseconds(6);
export const UNSUPPORTED_AFTER_LIVE_FOR = hoursToMilliseconds(24);

type Build = { version: string; builtAt: string };

export type BuildVersionCheck =
  | ({ outcome: 'available' } & Build)
  | { outcome: 'unavailable'; reason: string };

export type BuildVersionState =
  | { status: 'unknown' }
  | { status: 'current' }
  | { status: 'update-available'; latestVersion: string }
  | { status: 'unsupported'; latestVersion: string };

/**
 * Reads the build the hosting currently serves. Never throws: a missing file
 * comes back as the SPA's index.html with a 200, so the content type is
 * checked before the body is trusted.
 */
export const fetchBuildVersion = async (
  fetchFn: typeof fetch = fetch
): Promise<BuildVersionCheck> => {
  try {
    const response = await fetchFn(BUILD_VERSION_FILE_PATH, {
      cache: 'no-store',
    });
    if (!response.ok) {
      return { outcome: 'unavailable', reason: `HTTP ${response.status}` };
    }
    if (!response.headers.get('content-type')?.includes('application/json')) {
      return { outcome: 'unavailable', reason: 'Response is not JSON' };
    }
    const parsed = buildVersionFileSchema.safeParse(await response.json());
    if (!parsed.success) {
      return {
        outcome: 'unavailable',
        reason: 'Response does not describe a build',
      };
    }
    return { outcome: 'available', ...parsed.data };
  } catch (error) {
    return {
      outcome: 'unavailable',
      reason: error instanceof Error ? error.message : String(error),
    };
  }
};

const classify = (
  runningVersion: string,
  check: BuildVersionCheck,
  now: number
): BuildVersionState => {
  if (check.outcome === 'unavailable') {
    return { status: 'unknown' };
  }
  if (check.version === runningVersion) {
    return { status: 'current' };
  }
  const liveFor = now - Date.parse(check.builtAt);
  if (liveFor >= UNSUPPORTED_AFTER_LIVE_FOR) {
    return { status: 'unsupported', latestVersion: check.version };
  }
  if (liveFor >= UPDATE_AVAILABLE_AFTER_LIVE_FOR) {
    return { status: 'update-available', latestVersion: check.version };
  }
  return { status: 'current' };
};

const SEVERITY: Record<BuildVersionState['status'], number> = {
  unknown: 0,
  current: 0,
  'update-available': 1,
  unsupported: 2,
};

/**
 * Severity never decreases for a tab: once a notice is shown it stays, and a
 * later check can only raise it. The two no-notice states rank equally, so the
 * most recent of them wins.
 */
export const resolveBuildVersionState = (
  previous: BuildVersionState,
  runningVersion: string,
  check: BuildVersionCheck,
  now: number
): BuildVersionState => {
  const next = classify(runningVersion, check, now);
  return SEVERITY[next.status] >= SEVERITY[previous.status] ? next : previous;
};
